import React, { useEffect, useState } from "react";
import {
  AccountingAccount,
  AccountingAccountService,
} from "../services/accounting-account-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: Partial<AccountingAccount>) => void;
  item?: AccountingAccount | null;
}

export const AccountingAccountCreate: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  item,
}) => {
  const [form, setForm] = useState<Partial<AccountingAccount>>({
    code: "",
    name: "",
    description: "",
    english_name: "",
    type: 1,
    parent_id: 0,
  });
  const [parentOptions, setParentOptions] = useState<AccountingAccount[]>([]);
  const { fetch: fetchList } = AccountingAccountService.list();

  useEffect(() => {
    if (open) {
      if (item) setForm(item);
      else
        setForm({
          code: "",
          name: "",
          description: "",
          english_name: "",
          type: 1,
          parent_id: 0,
        });
    }
  }, [open, item]);

  // Load danh sách tài khoản cha nếu là "Chi tiết"
  useEffect(() => {
    const loadParents = async () => {
      if (!open || (form.type ?? 1) !== 2) return;
      const res = await fetchList({
        status: 1,
        type: 1,
        page: 1,
        limit: 1000,
      } as any);
      if (res && isSuccess(res)) { const data: any = res.data || {}; const list: AccountingAccount[] = Array.isArray(data.list) ? data.list : []; setParentOptions(list); } 
      else { setParentOptions([]); }
    };
    void loadParents();
  }, [open, form.type]);

  if (!open) return null;

  const handleSave = () => {
    // Validate cơ bản
    if (!form.code?.trim()) {
      showToast({ message: "Vui lòng nhập mã tài khoản", type: "error" });
      return;
    }
  
    if (!form.name?.trim()) {
      showToast({ message: "Vui lòng nhập tên tài khoản", type: "error" });
      return;
    }
  
    if (form.type === 2 && !form.parent_id) {
      showToast({
        message: "Tài khoản chi tiết cần chọn tài khoản cha",
        type: "error",
      });
      return;
    }
  
  
    try {
      onSave(form);
    } catch (error) {
      console.error(error);
      showToast({ message: "Đã xảy ra lỗi khi lưu dữ liệu", type: "error" });
    }
  };
  

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-center items-center z-50">
      <div className="bg-white rounded-2xl w-[520px] shadow-2xl overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-green-500 text-white px-5 py-3 flex justify-between items-center">
          <h3 className="text-lg font-semibold">
            {item ? "Cập nhật tài khoản" : "Thêm tài khoản kế toán"}
          </h3>
          <button
            onClick={onClose}
            className="text-white text-xl font-bold hover:opacity-80"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4">
            {/* Code */}
            <div>
              <label className="text-sm text-gray-600 font-medium">
                Mã tài khoản
              </label>
              <input
                type="text"
                placeholder="VD: 111, 131"
                className="border border-gray-300 rounded-lg w-full px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.code ?? ""}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>

            {/* Name */}
            <div>
              <label className="text-sm text-gray-600 font-medium">
                Tên tài khoản
              </label>
              <input
                type="text"
                className="border border-gray-300 rounded-lg w-full px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.name ?? ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </div>

            {/* Type */}
            <div>
              <label className="text-sm text-gray-600 font-medium">
                Loại tài khoản
              </label>
              <select
                className="border border-gray-300 rounded-lg w-full px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-green-500"
                value={form.type ?? 1}
                onChange={(e) => {
                  const t = Number(e.target.value);
                  setForm({
                    ...form,
                    type: t,
                    parent_id: t === 2 ? form.parent_id : 0,
                  });
                }}
              >
                <option value={1}>Tài khoản Tổng hợp</option>
                <option value={2}>Tài khoản Chi tiết</option>
              </select>
            </div>

            {/* Parent account */}
            {form.type === 2 && (
              <div>
                <label className="text-sm text-gray-600 font-medium">
                  Tài khoản cha
                </label>
                <select
                  className="border border-gray-300 rounded-lg w-full px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-green-500"
                  value={form.parent_id ?? 0}
                  onChange={(e) =>
                    setForm({ ...form, parent_id: Number(e.target.value) })
                  }
                >
                  <option value={0}>-- Chọn tài khoản tổng hợp --</option>
                  {parentOptions.map((acc) => (
                    <option key={acc.id} value={acc.id}>
                      {acc.code} - {acc.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Description */}
            <div className="col-span-2">
              <label className="text-sm text-gray-600 font-medium">
                Mô tả
              </label>
              <textarea
                placeholder="Mô tả ngắn gọn"
                className="border border-gray-300 rounded-lg w-full px-3 py-2 mt-1 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none h-24"
                value={form.description ?? ""}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
          >
            Hủy
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-green-600 text-white font-medium hover:bg-green-700 transition"
          >
            Lưu
          </button>
        </div>
      </div>
    </div>
  );
};
